import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getStreams,
  getStreamForEdit,
  getStreamNewOptions,
  createStream,
  updateStream,
  deleteStream,
  getCustomerOrganisations,
} from '../api';
import { streamKeys } from './queryKeys';
import type { StreamsFilters, StreamFormData } from '../types';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

export function useStreams(filters: StreamsFilters = {}) {
  return useQuery({
    queryKey: streamKeys.list(filters),
    queryFn: () => getStreams(filters),
  });
}

export function useStreamForEdit(streamId: string | null) {
  return useQuery({
    queryKey: streamKeys.edit(streamId!),
    queryFn: () => getStreamForEdit(streamId!),
    enabled: !!streamId,
  });
}

export function useStreamNewOptions(enabled = false) {
  return useQuery({
    queryKey: streamKeys.newOptions(),
    queryFn: getStreamNewOptions,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateStream() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StreamFormData) => createStream(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: streamKeys.lists() });
      toast.success('Stream created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create stream', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateStream() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<StreamFormData> }) =>
      updateStream(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: streamKeys.lists() });
      queryClient.invalidateQueries({ queryKey: streamKeys.edit(variables.id) });
      toast.success('Stream updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update stream', { description: getErrorMessage(error) });
    },
  });
}

export function useCustomerOrganisations() {
  return useQuery({
    queryKey: streamKeys.organisations(),
    queryFn: getCustomerOrganisations,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDeleteStream() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteStream(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: streamKeys.lists() });
      toast.success('Stream deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete stream', { description: getErrorMessage(error) });
    },
  });
}
